import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Pending-notifications boot.
 *
 * Mounted at the root of the app inside <BrowserRouter>. On the first
 * mount after the user is hydrated, fetches their unread notifications,
 * shows each one as a clickable sonner toast, then marks them read.
 *
 * Implementation notes
 * --------------------
 * - `_firedFor` is module-level so the gate survives React 18 StrictMode
 *   double-mount in dev, and so a single user only sees their pending
 *   toasts once per browser session even if NotificationBoot remounts
 *   during navigation.
 * - We reserve the slot SYNCHRONOUSLY before kicking off the fetch so
 *   StrictMode's second effect-pass sees the gate and skips — preventing
 *   a duplicate fetch + duplicate toasts.
 * - We do NOT cancel the in-flight fetch on cleanup. If we did, the
 *   first StrictMode pass would cancel its own work before the toasts
 *   render, and the second pass would already be gated → no toasts (the
 *   exact bug reported in test iteration_3).
 * - On failure we release the gate so a future mount can retry.
 */
const _firedFor = new Set();

export default function NotificationBoot() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || _firedFor.has(user.id)) return;
    _firedFor.add(user.id);
    (async () => {
      try {
        const { data } = await api.get("/notifications?unread_only=true&limit=20");
        if (!Array.isArray(data) || data.length === 0) return;
        for (const n of data) {
          toast.info(n.title, {
            description: n.message,
            duration: 8000,
            action: n.link
              ? { label: "Open", onClick: () => navigate(n.link) }
              : undefined,
            id: `notif-${n.id}`,
          });
        }
        api
          .post("/notifications/mark_read", { all: true })
          .catch((err) => console.warn("NotificationBoot mark_read failed:", err));
      } catch (err) {
        console.warn("NotificationBoot: failed to load unread notifications", err);
        // Release the gate so a future mount (e.g. after re-login) can retry.
        _firedFor.delete(user.id);
      }
    })();
  }, [user, navigate]);

  return null;
}
